import { useState } from "react";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Typography,
  Box,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useRole } from "../../contexts/RoleContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import {
  useTabPermissions,
  PERMISSION_MAP,
} from "../../hooks/useTabPermissions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHandHoldingHeart,
  faBoxesStacked,
  faChartLine,
  faLeaf,
  faHandshake,
  faLock,
  faCircleExclamation,
  faPenToSquare,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { ROLE_COLORS } from "../../constants/roles";
import { usePartnershipBadge } from "../../hooks/usePartnershipBadge";

function DonorNav({ collapsed }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { currentRole } = useRole();
  const { hasTabPermission } = useTabPermissions();
  const { claimedCount } = usePartnershipBadge();
  const { workspaceListing, workspaceHasChanges, clearWorkspace } = useWorkspace();
  const [toast, setToast] = useState({ open: false, message: "" });

  const currentColor = ROLE_COLORS[currentRole].primary;

  // small visual "!" badge (for collapsed mode)
  const ExclaimBadge = ({ tooltip }) => (
    <Tooltip title={tooltip} placement="right" arrow>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 16,
          height: 16,
          borderRadius: "999px",
          backgroundColor: "rgba(244, 67, 54, 0.95)",
          color: "#fff",
          fontSize: "11px",
          fontWeight: 900,
          lineHeight: 1,
          boxShadow: "0 6px 14px rgba(244, 67, 54, 0.25)",
        }}
      >
        !
      </Box>
    </Tooltip>
  );

  const navItems = [
    {
      label: t("Donate Materials"),
      path: "/donor/donate-materials",
      icon: faHandHoldingHeart,
    },
    {
      label: t("Allocate"),
      path: "/donor/allocate",
      icon: faBoxesStacked,
    },
    {
      label: t("Partnerships"),
      path: "/partnerships",
      icon: faHandshake,
    },
    {
      label: t("Inventory"),
      path: "/donor/inventory",
      icon: faChartLine,
    },
    {
      label: t("ESG & Carbon"),
      path: "/donor/ghg",
      icon: faLeaf,
    },
  ];

  if (navItems.length === 0) return null;

  const handleNavigation = (item, hasPermission) => {
    if (hasPermission) {
      navigate(item.path);
    } else {
      setToast({
        open: true,
        message: t("You don't have access to this feature"),
      });
    }
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  return (
    <>
      {!collapsed && (
        <Box className="nav-section-header">
          <Typography
            className="nav-section-title"
            sx={{ color: `${currentColor} !important` }}
          >
            {t("Features")}
          </Typography>
        </Box>
      )}
      <List sx={{ pt: 0, pb: 0 }}>
        {navItems.map((item, index) => {
          const permissionInfo = PERMISSION_MAP[item.path];
          const hasPermission = permissionInfo
            ? hasTabPermission(
                permissionInfo.section,
                permissionInfo.permission,
              )
            : true;
          const isActive = location.pathname === item.path;
          const isDisabled = !hasPermission;

          const isPartnership = item.path === "/partnerships";
          const showBadge = isPartnership && claimedCount > 0 && !isDisabled;
          const badgeTooltip = `${claimedCount} new partner${claimedCount === 1 ? "" : "s"} claimed`;

          return (
            <>
              <ListItem
                key={index}
                disablePadding
                sx={{
                  margin: "2px 8px",
                  borderRadius: "8px",
                  width: "auto",
                }}
              >
                <Tooltip
                  title={
                    isDisabled
                      ? t("You don't have access to this feature")
                      : collapsed
                        ? item.label
                        : ""
                  }
                  placement="right"
                  arrow
                >
                  <span style={{ width: "100%" }}>
                    <ListItemButton
                      onClick={() => handleNavigation(item, hasPermission)}
                      sx={{
                        justifyContent: collapsed ? "center" : "flex-start",
                        px: collapsed ? 0 : 2,
                        py: 1.25,
                        borderRadius: "8px",
                        minHeight: "44px",
                        opacity: isDisabled ? 0.5 : 1,
                        cursor: isDisabled ? "not-allowed" : "pointer",
                        backgroundColor:
                          isActive && !isDisabled
                            ? "rgba(102, 157, 46, 0.18)"
                            : "transparent",
                        "&:hover": {
                          backgroundColor:
                            isActive && !isDisabled
                              ? "rgba(102, 157, 46, 0.22)"
                              : isDisabled
                                ? "rgba(255, 255, 255, 0.03)"
                                : "rgba(255, 255, 255, 0.06)",
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: collapsed ? "auto" : 40,
                          justifyContent: "center",
                          color: isDisabled
                            ? "#666"
                            : isActive
                              ? currentColor
                              : "#b0b0b0",
                          fontSize: "18px",
                        }}
                      >
                        <Box sx={{ position: "relative", display: "inline-flex" }}>
                          <FontAwesomeIcon icon={item.icon} />
                          {collapsed && showBadge && (
                            <Box sx={{ position: "absolute", top: -6, right: -10 }}>
                              <ExclaimBadge tooltip={badgeTooltip} />
                            </Box>
                          )}
                        </Box>
                      </ListItemIcon>
                      {!collapsed && (
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                width: "100%",
                              }}
                            >
                              <span>{item.label}</span>
                              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                                {showBadge && (
                                  <Tooltip title={badgeTooltip} placement="right" arrow>
                                    <Box sx={{ display: "inline-flex", alignItems: "center", color: "rgba(244, 67, 54, 0.95)" }}>
                                      <FontAwesomeIcon icon={faCircleExclamation} style={{ fontSize: "14px" }} />
                                    </Box>
                                  </Tooltip>
                                )}
                                {isDisabled && (
                                  <FontAwesomeIcon
                                    icon={faLock}
                                    style={{
                                      fontSize: "12px",
                                      marginLeft: "4px",
                                      opacity: 0.7,
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>
                          }
                          sx={{
                            "& .MuiTypography-root": {
                              fontFamily: "Poppins, sans-serif",
                              fontSize: "0.9375rem",
                              color: isDisabled
                                ? "#666"
                                : isActive
                                  ? currentColor
                                  : "#e0e0e0",
                              fontWeight: isActive && !isDisabled ? 600 : 400,
                            },
                          }}
                        />
                      )}
                    </ListItemButton>
                  </span>
                </Tooltip>
              </ListItem>

              {/* workspace child tab */}
              {item.path === "/donor/donate-materials" && workspaceListing && !collapsed && (
                <ListItem
                  disablePadding
                  sx={{ margin: "2px 8px 4px 28px", width: "auto" }}
                >
                  <ListItemButton
                    onClick={() => navigate("/donor/workspace")}
                    sx={{
                      px: 1.5,
                      py: 1.25,
                      minHeight: "44px",
                      borderRadius: "8px",
                      width: "100%",
                      border: location.pathname === "/donor/workspace"
                        ? "1px solid rgba(102, 157, 46, 0.45)"
                        : "1px solid rgba(255, 255, 255, 0.1)",
                      backgroundColor: location.pathname === "/donor/workspace"
                        ? "rgba(102, 157, 46, 0.18)"
                        : "transparent",
                      "&:hover": {
                        backgroundColor: location.pathname === "/donor/workspace"
                          ? "rgba(102, 157, 46, 0.22)"
                          : "rgba(255, 255, 255, 0.05)",
                        borderColor: location.pathname === "/donor/workspace"
                          ? "rgba(102, 157, 46, 0.45)"
                          : "rgba(255,255,255,0.18)",
                        "& .ws-close-btn": { opacity: 1 },
                      },
                      transition: "background 0.15s, border-color 0.15s",
                    }}
                  >
                    <ListItemIcon sx={{
                      minWidth: 32,
                      justifyContent: "center",
                      color: location.pathname === "/donor/workspace" ? currentColor : "#b0b0b0",
                      fontSize: "15px",
                    }}>
                      <FontAwesomeIcon icon={faPenToSquare} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Box sx={{
                              fontFamily: "Poppins, sans-serif",
                              fontSize: "0.875rem",
                              fontWeight: location.pathname === "/donor/workspace" ? 600 : 400,
                              color: location.pathname === "/donor/workspace" ? currentColor : "#b0b0b0",
                              whiteSpace: "nowrap",
                            }}>
                              {t("Workspace")}
                            </Box>
                            <Box sx={{
                              fontFamily: "Poppins, sans-serif",
                              fontSize: "0.7rem",
                              color: "#555",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              lineHeight: 1.3,
                            }}>
                              {workspaceListing.material || workspaceListing.item_name || ""}
                            </Box>
                          </Box>
                          <Box
                            className="ws-close-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (workspaceHasChanges) {
                                navigate("/donor/workspace");
                              } else {
                                clearWorkspace();
                                if (location.pathname === "/donor/workspace") navigate("/donor/donate-materials");
                              }
                            }}
                            sx={{
                              opacity: 0,
                              width: 18,
                              height: 18,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: "4px",
                              flexShrink: 0,
                              ml: 0.5,
                              color: "#666",
                              "&:hover": { color: "#ef4444", backgroundColor: "rgba(239,68,68,0.1)" },
                              transition: "opacity 0.15s, color 0.15s, background 0.15s",
                            }}
                          >
                            <FontAwesomeIcon icon={faTimes} style={{ fontSize: "10px" }} />
                          </Box>
                        </Box>
                      }
                      sx={{ my: 0 }}
                    />
                  </ListItemButton>
                </ListItem>
              )}
            </>
          );
        })}
      </List>

      {/* Toast for permission denied */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseToast}
          severity="warning"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default DonorNav;
